export interface ExceptionFieldSnapshot {
  title: string;
  description: string;
  requestedDocumentFileName: string | null;
  finalDocumentFileName: string | null;
  renewalDate: string | null;
}

export interface ExceptionTimelineEditEntry {
  editedAt: string;
  titleChanged: boolean;
  descriptionChanged: boolean;
  documentChanged: boolean;
  renewalDateChanged: boolean;
  renewalDate: string | null;
  /** Renewal date as of just before this edit - used to detect "this had already
   *  expired before being renewed again" so the timeline can show that waypoint. */
  previousRenewalDate: string | null;
  documentFileName: string | null;
}

const SNAPSHOT_PREFIX = "grbes_exception_snapshot_";
const EDITS_PREFIX = "grbes_exception_timeline_edits_";

export class ExceptionTimelineTracker {
  static buildSnapshot(source: any): ExceptionFieldSnapshot {
    const attachments = source?.addendumAttachment || [];
    const requested = attachments.find(
      (attachment: any) => attachment?.isRequestedFile === true
    );
    const finalAttachment = attachments.find(
      (attachment: any) => attachment?.isRequestedFile === false
    );
    const renewal = source?.exceptionRenewalDate || source?.expiryDate;

    return {
      title: (source?.title || "").trim(),
      description: (source?.description || "").trim(),
      requestedDocumentFileName: requested?.fileName || null,
      finalDocumentFileName: finalAttachment?.fileName || null,
      renewalDate: renewal ? new Date(renewal).toISOString() : null,
    };
  }

  static getSnapshot(addendumId: number): ExceptionFieldSnapshot | null {
    if (!addendumId) {
      return null;
    }

    const raw = localStorage.getItem(`${SNAPSHOT_PREFIX}${addendumId}`);
    return raw ? (JSON.parse(raw) as ExceptionFieldSnapshot) : null;
  }

  static saveSnapshot(
    addendumId: number,
    snapshot: ExceptionFieldSnapshot
  ): void {
    if (!addendumId) {
      return;
    }

    localStorage.setItem(
      `${SNAPSHOT_PREFIX}${addendumId}`,
      JSON.stringify(snapshot)
    );
  }

  static getEditEntries(addendumId: number): ExceptionTimelineEditEntry[] {
    if (!addendumId) {
      return [];
    }

    const raw = localStorage.getItem(`${EDITS_PREFIX}${addendumId}`);
    return raw ? (JSON.parse(raw) as ExceptionTimelineEditEntry[]) : [];
  }

  static recordEdit(
    addendumId: number,
    baseline: ExceptionFieldSnapshot,
    submitted: any
  ): boolean {
    if (!addendumId || !baseline) {
      return false;
    }

    const current = this.buildSnapshot(submitted);
    const titleChanged = baseline.title !== current.title;
    const descriptionChanged = baseline.description !== current.description;
    const documentChanged = this.hasDocumentChanged(baseline, current, submitted);
    const renewalDateChanged = baseline.renewalDate !== current.renewalDate;

    if (!titleChanged && !descriptionChanged && !documentChanged && !renewalDateChanged) {
      this.saveSnapshot(addendumId, current);
      return false;
    }

    const entry: ExceptionTimelineEditEntry = {
      editedAt: new Date().toISOString(),
      titleChanged,
      descriptionChanged,
      documentChanged,
      renewalDateChanged,
      renewalDate: current.renewalDate,
      previousRenewalDate: baseline.renewalDate,
      documentFileName:
        current.requestedDocumentFileName ||
        current.finalDocumentFileName ||
        null,
    };

    const entries = this.getEditEntries(addendumId);
    entries.push(entry);
    localStorage.setItem(`${EDITS_PREFIX}${addendumId}`, JSON.stringify(entries));
    this.saveSnapshot(addendumId, current);
    return true;
  }

  private static hasDocumentChanged(
    baseline: ExceptionFieldSnapshot,
    current: ExceptionFieldSnapshot,
    submitted: any
  ): boolean {
    if (baseline.requestedDocumentFileName !== current.requestedDocumentFileName) {
      return true;
    }

    if (baseline.finalDocumentFileName !== current.finalDocumentFileName) {
      return true;
    }

    return (submitted?.addendumAttachment || []).some(
      (attachment: any) => !!attachment?.attachmentContentAsBase64
    );
  }
}
